import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "./api";
import { FRONTEND_VERSION } from "../components/landing/VersionBadge";

const POLL_INTERVAL_MS = 60_000;       // Every minute, ping /api/version
const GRACE_BEFORE_RELOAD_MS = 10_000; // Give user 10s to dismiss before forcing reload
const STORAGE_KEY = "otp_version_dismissed";

/**
 * Polls the backend for its build version. If it diverges from the
 * FRONTEND_VERSION baked into the current JS bundle, the user is on a stale
 * build — we surface a toast and auto-reload with a cache-buster so the
 * browser fetches the fresh index.html (which then references the new chunks).
 *
 * Mount this once at the top of the React tree.
 *
 * Implementation note: `alertedRef` is a ref (not state) on purpose. Mutating
 * a ref does not trigger a re-render, so the reload timeout set inside the
 * effect is not torn down by React when we mark the toast as shown.
 */
export function useVersionDriftReloader() {
    const alertedRef = useRef(false);
    const reloadTimerRef = useRef(null);

    useEffect(() => {
        let cancelled = false;

        const forceReload = () => {
            // Append a single-use query string to bypass any intermediate cache.
            const url = new URL(window.location.href);
            url.searchParams.set("v", Date.now().toString());
            window.location.replace(url.toString());
        };

        const check = async () => {
            try {
                const r = await api.get("/version", { params: { t: Date.now() } });
                const backendVersion = r?.data?.version;
                if (!backendVersion || cancelled) return;
                if (backendVersion === FRONTEND_VERSION) return;
                if (alertedRef.current) return;
                // Respect prior dismissal in the same tab session — never spam.
                if (sessionStorage.getItem(STORAGE_KEY) === backendVersion) return;

                alertedRef.current = true;
                toast("A newer version is available", {
                    description: `Updating to v${backendVersion} in 10 seconds…`,
                    action: {
                        label: "Refresh now",
                        onClick: forceReload,
                    },
                    cancel: {
                        label: "Later",
                        onClick: () => {
                            sessionStorage.setItem(STORAGE_KEY, backendVersion);
                            if (reloadTimerRef.current) {
                                clearTimeout(reloadTimerRef.current);
                                reloadTimerRef.current = null;
                            }
                        },
                    },
                    duration: GRACE_BEFORE_RELOAD_MS,
                });
                reloadTimerRef.current = setTimeout(() => {
                    if (sessionStorage.getItem(STORAGE_KEY) !== backendVersion) forceReload();
                }, GRACE_BEFORE_RELOAD_MS);
            } catch (e) {
                // Backend unreachable — nothing to compare against. Stay silent.
                console.debug("Version check skipped", e);
            }
        };

        check();
        const id = setInterval(check, POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            clearInterval(id);
            if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
        };
    }, []);
}
